<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { useDateFormat } from '@vueuse/core'
  import { useRouter } from 'vue-router'
  import { usePresentationsMenuStore } from '@/stores/list-item-menu'
  import { usePresentationsBarStore } from '@/stores/floating-bars'
  import { usePresentationsStore } from '@/stores/presentations'
  import { useSnackbarStore } from '@/stores/snackbar'
  import type { TPresentationListItem } from '@/types/presentation'
  import DesHover from '@/components/DesHover.vue'
  import DesHoverMenu from '@/components/DesHoverMenu.vue'
  import DesButtonIcon from '@/components/button/DesButtonIcon.vue'
  import DesEditName from '@/components/DesEditName.vue'
  import DesCheckbox from '@/components/form/DesCheckbox.vue'

  const props = defineProps<{
    item: TPresentationListItem
    disableCheckboxes?: boolean
  }>()

  const presentations = usePresentationsStore()
  const bar = usePresentationsBarStore()
  const snack = useSnackbarStore()
  const router = useRouter()
  const { open } = usePresentationsMenuStore()

  const formatDate = (date: string) => useDateFormat(date, 'MMMM DD, YYYY', { locales: 'en-US' })
  const tile = ref<HTMLElement | null>(null)
  const checked = ref<boolean>(bar.is(props.item))

  const edit = (item: TPresentationListItem) => {
    router.push(`/presentation-editor/${item.id}`)
  }

  const renamePresentation = async (item: TPresentationListItem, title: string) => {
    const oldName = item.title
    item.title = title

    const res = await presentations.rename(item.id, title)

    if (res === false) {
      snack.add('Failed to rename presentation', { type: 'danger' })
      item.title = oldName
    }
  }

  watch(
    () => bar.list.length,
    () => (checked.value = bar.is(props.item)),
  )
</script>

<template>
  <div class="flex flex-col gap-1.5" ref="tile">
    <div
      class="relative h-52 px-4 pt-4 bg-white-gray rounded-xl justify-center items-center inline-flex overflow-hidden cursor-pointer border"
      :class="checked ? 'border-blue-50' : 'border-transparent'"
    >
      <div class="h-44 flex-col justify-center items-center inline-flex">
        <img
          v-if="item.thumbnail"
          class="max-h-full max-w-full w-auto h-auto rounded-md shadow-sm"
          :src="item.thumbnail"
          loading="lazy"
          alt=""
        />
        <img v-else class="h-16 w-auto opacity-60" :src="'/dashboard/new-project/type-presentation.png'" alt="" />
      </div>

      <DesHover data-sortable-handler @click.stop="edit(item)">
        <DesHoverMenu>
          <DesButtonIcon @click.stop="tile && open(item, tile)" icon="fa-light fa-ellipsis" />
        </DesHoverMenu>
        <DesCheckbox v-model="checked" @click.stop.prevent="bar.toggle(item)" v-if="disableCheckboxes !== true" />
      </DesHover>
      <DesHover @click.stop.prevent="bar.toggle(item)" v-if="bar.list.length > 0" locked>
        <DesCheckbox v-model="checked" v-if="disableCheckboxes !== true" />
      </DesHover>
    </div>

    <div>
      <DesEditName :name="item.title" @edit="(newValue: string) => renamePresentation(item, newValue)" />
      <div class="mt-1 text-black-40 text-xs leading-none">
        {{ item.updated_at ? formatDate(item.updated_at).value : '' }}
      </div>
    </div>
  </div>
</template>
